<template>
    <div class="ph-quiz-table">
        <v-data-table-server
            :items="items"
            :headers="headers"
            :loading="loading"
            :loading-text="$t('common.loading')"
            :no-data-text="$t('common.noDataText')"
            :items-per-page-text="`${$t('common.rowsPerPage')}`"
            :page-text="`${currentPage} ${$t('common.of')} ${lastPage}`"
            :items-per-page-options="itemsPerPageOptions"
            :items-length="total"
            :options="options"
            fixed-header
            :height="items.length > 10 ? '60vh' : '100%'"
            @update:options="optionsUpdate"
        >
            <template #loading>
                <div class="ph-quiz-table__loading">{{ $t('common.loading') }}</div>
            </template>

            <template #top>
                <div class="kasanowa-top-filter-wrapper">
                    <KTextField
                        id="search"
                        v-model="search"
                        name="search"
                        label=""
                        :placeholder="$t('common.search')"
                        hide-details
                        clearable
                    />
                </div>
            </template>

            <template #item.name="elem">
                <div v-if="elem.item.name != null">{{ elem.item.name }}</div>
            </template>

            <template #item.price="elem">
                <div v-if="elem.item.price != null">{{ elem.item.price }} PLN</div>
            </template>

            <template #footer.prepend>
                <div class="kasanowa-total-table-counter">{{ $t('common.total') }}: {{ total }}</div>
            </template>
        </v-data-table-server>
    </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import router from '@/router';
import { itemsPerPageOptions } from '@/hooks/TableSettingsHooks';
import SalesRepApi from '@/api/v1/SalesRepApi';
import { toast } from 'vue3-toastify';
import type { ITableHeader, ITableOptions } from '@/models/TableSettingsModel';
import type {
    ISalesRepQuizResponse,
} from '@/models/SalesRepModel';
import KTextField from '@/components/atoms/KTextField.vue';

const headers = ref<ITableHeader[]>([
    {
        title: t('salesRep.quizName'),
        align: 'start',
        sortable: false,
        key: 'name',
        width: '200px',
        minWidth: '200px',
    },
    {
        title: t('salesRep.quizPrice'),
        align: 'center',
        sortable: false,
        key: 'price',
        width: '',
        minWidth: '160px',
    },
]);
const loading = ref<boolean>(false);
const currentPage = ref<number>(0);
const lastPage = ref<number>(0);
const total = ref<number>(0);
const items = ref<ISalesRepQuizResponse[]>([]);
const salesRepApi = new SalesRepApi();
const salesRepQuizList = async() => {
    loading.value = true;
    try {
        const result = await salesRepApi.salesRepQuizList(
            router.currentRoute.value.params.id,
            options.value.page,
            options.value.itemsPerPage,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].key : null,
            options.value.sortBy.length > 0 ? options.value.sortBy[0].order : null,
            search.value,
        );
        if (!result.isSuccess) {
            toast.error(t('salesRep.toast.errorSalesRepQuizList'));
            loading.value = false;
            return;
        }
        currentPage.value = result.payload.current_page;
        lastPage.value = result.payload.last_page;
        total.value = result.payload.total;
        items.value = result.payload.data;
    } catch (e) {
        toast.error(t('salesRep.toast.errorSalesRepCardsList'));
        loading.value = false;
        return;
    }
    loading.value = false;
};
const options = ref<ITableOptions>({
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    groupBy: [],
    search: null,
});
const optionsUpdate = (val:ITableOptions) => {
    options.value.page = val.page;
    options.value.itemsPerPage = val.itemsPerPage;
    options.value.search = val.search;
    options.value.groupBy = val.groupBy;
    options.value.sortBy = val.sortBy;
    salesRepQuizList();
};

const search = ref<string | null>(null);
const searchInterval = ref<any>(undefined);
watch(() => search.value, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        clearTimeout(searchInterval.value);
    }
    searchInterval.value = setTimeout(() => {
        salesRepQuizList();
    }, 700);
}, {
    deep: true
});
</script>

<style scoped lang="scss">
.ph-quiz-table {
    &__loading {
        opacity: 0.6;
    }
}
</style>
